import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import { useI18n } from "../../lib/i18n";
import type { MapClub } from "./club-map-impl";

// Leaflet touches `window`/`document` at module load time, so it must never
// be evaluated during SSR. React.lazy() defers the import; the `mounted`
// gate below keeps the <Suspense> branch (and therefore the import) out of
// the tree entirely until after the client has mounted, so it never runs
// server-side or during the first (SSR-matching) client render.
const MultiClubMapLazy = lazy(() =>
  import("./club-map-impl").then((m) => ({ default: m.MultiClubMapImpl })),
);

function useMounted(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

// A lazy chunk that 404s (stale deploy, flaky network, an extension blocking
// the request) throws inside render, and without a boundary here that throw
// travels up and replaces the entire results page with a blank error screen.
// It happened in production: one missing map bundle took the whole club list
// with it. The map is the least important thing on any of these pages, so it
// gets to fail alone and quietly.
class MapBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function MapFailed() {
  const { t } = useI18n();
  return (
    <div className="flex h-full w-full items-center justify-center p-6">
      <p className="li-mono max-w-[36ch] text-center text-[12px] leading-relaxed text-[#5B6472]">
        {t("map_failed")}
      </p>
    </div>
  );
}

export function ClubsMap({
  clubs,
  onSelect,
}: {
  clubs: MapClub[];
  onSelect: (slug: string) => void;
}) {
  const mounted = useMounted();

  return (
    <div className="relative isolate z-0 h-[70dvh] min-h-[420px] w-full overflow-hidden rounded-xl border border-[#1C2430]/10 bg-[#E7E2D6]">
      {mounted ? (
        <MapBoundary fallback={<MapFailed />}>
          <Suspense fallback={null}>
            <MultiClubMapLazy clubs={clubs} onSelect={onSelect} />
          </Suspense>
        </MapBoundary>
      ) : null}
    </div>
  );
}
